import { observable, computed, makeObservable } from 'mobx'
import { TeamDTO } from '../dtos/team'
import { DateZero, maybeParseDate } from 'utils/date-util'
import TeamMember from './TeamMember'
import { Model } from 'infra/model'
import { PrimeTeamMember } from 'iam/TeamMemberStore'
import { Id } from 'types/definitions'
import once from 'lodash/once'
import { generateAvatarFallback } from 'utils/image-util'
import { PrimeWorkspace } from '../WorkspaceStore'
import Workspace from './Workspace'

export interface TeamAddress {
  streetAddress?: string
  zipCode?: string
  city?: string
  state?: string
  country?: string
}

export default class Team extends Model<TeamDTO, Team> {
  @observable id = Id.none
  @observable name = ''
  @observable slug = ''
  @observable picture = ''
  @observable inactive = false
  @observable dateCreated: Date = DateZero()
  @observable dateModified: Date = DateZero()
  @observable owner = TeamMember.none
  @observable teamPurpose = ''
  @observable address: TeamAddress = {}
  @observable workspace = Workspace.none

  static get none(): Team {
    return lazyNone()
  }

  constructor(
    private primeTeamMember: PrimeTeamMember,
    private primeWorkspace: PrimeWorkspace,
  ) {
    super()
    makeObservable(this)
  }

  @computed
  get avatar() {
    return this.picture || generateAvatarFallback(this.name)
  }

  parse(attrs: Partial<TeamDTO>): Partial<Team> {
    const id = attrs.id || this.id
    return {
      id,
      dateCreated: maybeParseDate(attrs.date_created),
      dateModified: maybeParseDate(attrs.date_modified),
      name: attrs.name,
      slug: attrs.slug,
      teamPurpose: attrs.team_purpose,
      picture: attrs.picture,
      workspace: this.primeWorkspace({ id: attrs.workspace_id }),
      address: attrs.address && {
        zipCode: attrs.address.zip_code,
        city: attrs.address.city,
        state: attrs.address.state,
        streetAddress: attrs.address.street_address,
        country: attrs.address.country,
      },
      owner: attrs.owner_id
        ? this.primeTeamMember({
            user_id: attrs.owner_id,
            user_public_id: attrs.owner_public_id,
            team_id: id,
            workspace_id: attrs.workspace_id || this.workspace.id,
          })
        : undefined,
    }
  }
}

const lazyNone = once(() =>
  new Team(
    () => TeamMember.none,
    () => Workspace.none,
  ).set({
    name: 'Team',
    slug: 'team',
  }),
)
